import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightIcon from "@tCentro/Icons/ArrowRightIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    description = "description",
    linktitle = "linktitle",
    link = "link",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.description]: string;
        [fields.linktitle]: string;
        destination: LinkFieldValue;
    };
}

export interface CENFeatureLayoutStyles {
    containerWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    subtitleWrapper?: InjectableCss;
    linkIconWrapper?: InjectableCss;
}

export const cenFeatureLayoutStyle: CENFeatureLayoutStyles = {
    containerWrapper: {
        css: css`
            border: 4px solid #f2f2f2;
            background: #ffffff;
            padding: 32px;
            gap: 16px;
            height: 100%;
        `,
    },
    titleWrapper: {
        css: css`
            font-family: "Poppins";
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.largest};
            color: ${getProp("theme.typography.p.color")};
            line-height: 32px;
        `,
    },
    subtitleWrapper: {
        css: css`
            font-family: "Poppins";
            display: block;
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.large};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            margin-top: 0.4rem;
        `,
    },
    linkIconWrapper: {
        css: css`
            padding-left: 26px;
            span {
                svg path {
                    fill: #0f96d9;
                }
            }
        `,
    },
};

const styles = cenFeatureLayoutStyle;

export const CENFeatureLayout: FC<OwnProps> = ({ fields }) => {
    const links = useGetLink(fields.destination);

    return (
        <StyledWrapper {...styles.containerWrapper}>
            <Typography {...styles.titleWrapper}>{parse(transformContentHtml(fields.title), parserOptions)}</Typography>
            <Typography {...styles.subtitleWrapper}>
                {parse(transformContentHtml(fields.subtitle), parserOptions)}
            </Typography>
            <Typography>{parse(transformContentHtml(fields.description), parserOptions)}</Typography>
            <Typography {...styles.linkIconWrapper}>
                <Link href={links.url}>
                    {fields.linktitle} {fields.linktitle && <ArrowRightIcon />}
                </Link>
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENFeatureLayout,
    definition: {
        group: "Basic",
        displayName: "Feature Layout",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.subtitle,
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.description,
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.linktitle,
                displayName: "linktitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "destination",
                displayName: "Destination",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
